import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { BigCheck, StepNav } from "@/components/wizard/Stepper";

const F = ({ id, label, className = "", ...p }) => (
  <div className={className}>
    <label htmlFor={id} className="label">{label}</label>
    <input id={id} className="field" data-testid={`shipping-${id}-input`} {...p} />
  </div>
);

const STATES = [["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "Washington, D.C."], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"]];
const NO_SHIP = ["AK", "HI"];

export const StepShipping = ({ order, go, back }) => {
  const { user, setUser } = useAuth();
  const [a, setA] = useState(order.shipping || { name: user.name || "", line1: "", line2: "", city: "", state: "", zip: "", country: "US" });
  const [phone, setPhone] = useState(user.phone || "");
  const [ref, setRef] = useState(order.referral_code || "");
  const [yt, setYt] = useState(!!order.consents?.youtube_unlisted);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const st = (a.state || "").toUpperCase();
  const blocked = NO_SHIP.includes(st) || (st && !STATES.some(([c]) => c === st));
  const ready = a.name && a.line1 && a.city && st && !blocked && /^\d{5}(-\d{4})?$/.test(a.zip) && phone.length >= 7 && yt;

  const next = async () => {
    setBusy(true);
    try {
      if (phone !== user.phone) setUser((await api.put("/auth/profile", { phone })).data);
      await go(7, { shipping: { ...a, state: a.state.toUpperCase() }, referral_code: ref.trim(), consent_youtube_unlisted: yt });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h2 className="text-3xl font-bold text-[#4A2A18]">Where should we send your keepsakes?</h2>
      <p className="mt-3 text-lg text-[#4A3B32]"><b className="text-[#2E6930]">FREE standard shipping</b> to the lower 48 states and Washington, D.C.</p>
      <div className="mt-8 grid gap-5 sm:grid-cols-6">
        <F id="name" label="Full name" className="sm:col-span-6" value={a.name} onChange={set("name")} autoComplete="name" />
        <F id="phone" label="Phone number" className="sm:col-span-6" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
        <F id="line1" label="Street address" className="sm:col-span-6" value={a.line1} onChange={set("line1")} autoComplete="address-line1" />
        <F id="line2" label="Apartment, suite (optional)" className="sm:col-span-6" value={a.line2 || ""} onChange={set("line2")} autoComplete="address-line2" />
        <F id="city" label="City" className="sm:col-span-3" value={a.city} onChange={set("city")} autoComplete="address-level2" />
        <div className="sm:col-span-3">
          <label htmlFor="state" className="label">State</label>
          <select id="state" className="field" value={st} onChange={set("state")} autoComplete="address-level1" data-testid="shipping-state-select">
            <option value="">Choose your state</option>
            {STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </div>
        <F id="zip" label="ZIP code" className="sm:col-span-3" inputMode="numeric" maxLength={10} value={a.zip} onChange={set("zip")} autoComplete="postal-code" />
      </div>
      {blocked && (
        <div role="alert" className="mt-5 rounded-xl border-2 border-[#A83232] bg-[#FDF2F2] p-4 text-lg text-[#7F1D1D]" data-testid="shipping-blocked-message">
          <b>Sorry, we can't ship to Alaska or Hawaii yet.</b> We ship FREE to the lower 48 states and Washington, D.C. You can send the keepsakes to a family member there and they can pass them along.
        </div>
      )}
      <div className="mt-8">
        <label htmlFor="ref" className="label">Referral code <span className="font-normal text-[#8C7A6B]">(optional - if a friend shared one)</span></label>
        <input id="ref" className="field sm:max-w-xs" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="referral-code-input" />
      </div>
      <div className="mt-8 rounded-xl border-2 border-[#E6DEC6] bg-[#FAF6F0] p-5" data-testid="youtube-consent-explainer">
        <p className="text-lg font-bold text-[#2C1E16]">About your video on YouTube</p>
        <p className="mt-2 text-lg leading-relaxed text-[#4A3B32]">After you pay, we upload your video to the Voices 2 Remember YouTube channel as <b>unlisted</b> and marked "not made for kids". Unlisted videos don't appear in search, but <b>anyone who has the link or scans a sticker can watch it and share it</b>.</p>
      </div>
      <div className="mt-4">
        <BigCheck checked={yt} onChange={setYt} testid="consent-youtube-checkbox">I agree to have my video uploaded to YouTube as unlisted, and I understand anyone with the link can watch and share it.</BigCheck>
      </div>
      <StepNav back={back} next={next} disabled={!ready} busy={busy} />
    </div>
  );
};
